<script>
/**
 * @component UrgencyCreateCase
 * @description Creación de casos de urgencia desde admin/operador. Permite buscar un
 *              paciente existente o crear uno nuevo, seleccionar especialidad de urgencia
 *              y motivo de consulta, y enviar el caso a la cola. Flujo paso a paso (stepper).
 *              Diseño basado en PatientList (paso 1) y landing de consulta (paso 2).
 * @example <UrgencyCreateCase />
 */
import urgencyQueueService from "@/services/urgency-queue";
import urgencyConfigService from "@/services/urgency-config";
import BaseSubNav from "@/components/ui/BaseSubNav.vue";
import { mapGetters } from "vuex";

export default {
  name: "UrgencyCreateCase",
  components: { BaseSubNav },

  data() {
    return {
      step: 1,
      // Paso 1 — Paciente
      searchQuery: "",
      searchResults: [],
      isSearching: false,
      searchTimeout: null,
      selectedPatient: null,
      isNewPatient: false,
      newPatientForm: {
        firstName: "",
        lastName: "",
        email: "",
        governmentIdNumber: "",
        phone: "",
      },
      newPatientErrors: {},
      // Paso 2 — Datos del caso
      urgencyConfigs: [],
      medicalSpecialtyId: "",
      reason: "",
      formErrors: {},
      // Paso 3 — Confirmación
      isSubmitting: false,
    };
  },

  computed: {
    ...mapGetters(["isAdmin", "isHealthAdmin"]),

    /**
     * @description Si el usuario tiene rol administrativo
     * @returns {boolean}
     */
    isAdminLike() {
      return this.isAdmin || this.isHealthAdmin;
    },

    /**
     * @description Configs habilitadas para el selector de especialidad
     * @returns {Array}
     */
    enabledConfigs() {
      return this.urgencyConfigs.filter(
        (c) => c.isEnabled !== false && c.medicalSpecialty
      );
    },

    /**
     * @description Nombre de la especialidad seleccionada para el resumen
     * @returns {string}
     */
    selectedSpecialtyName() {
      if (!this.medicalSpecialtyId) return "";
      const config = this.enabledConfigs.find(
        (c) => c.medicalSpecialty._id === this.medicalSpecialtyId
      );
      return config?.medicalSpecialty?.name || "";
    },

    /**
     * @description Nombre del paciente para el resumen
     * @returns {string}
     */
    patientDisplayName() {
      if (this.selectedPatient) {
        const p = this.selectedPatient;
        return `${p.firstName || ""} ${p.lastName || ""}`.trim() || "—";
      }
      if (this.isNewPatient) {
        const f = this.newPatientForm;
        return `${f.firstName || ""} ${f.lastName || ""}`.trim() || "—";
      }
      return "—";
    },

    /**
     * @description DNI del paciente para el resumen
     * @returns {string}
     */
    patientDisplayDni() {
      if (this.selectedPatient) {
        return (
          this.selectedPatient.dni ||
          this.selectedPatient.governmentId?.number ||
          "—"
        );
      }
      if (this.isNewPatient) {
        return this.newPatientForm.governmentIdNumber || "—";
      }
      return "—";
    },

    /**
     * @description Email del paciente para el resumen
     * @returns {string}
     */
    patientDisplayEmail() {
      if (this.selectedPatient) {
        return this.selectedPatient.email || "";
      }
      if (this.isNewPatient) {
        return this.newPatientForm.email || "";
      }
      return "";
    },

    /**
     * @description Si el paso 1 está completo (paciente seleccionado o nuevo válido)
     * @returns {boolean}
     */
    isStep1Valid() {
      if (this.selectedPatient) return true;
      if (this.isNewPatient) {
        const f = this.newPatientForm;
        return !!(f.firstName && f.lastName && f.email && f.governmentIdNumber);
      }
      return false;
    },

    /**
     * @description Si el paso 2 está completo
     * @returns {boolean}
     */
    isStep2Valid() {
      return !!this.medicalSpecialtyId;
    },
  },

  created() {
    this.fetchConfigs();
  },

  beforeDestroy() {
    if (this.searchTimeout) clearTimeout(this.searchTimeout);
  },

  methods: {
    /**
     * @description Carga las configuraciones de urgencia habilitadas
     * @returns {Promise<void>}
     */
    async fetchConfigs() {
      try {
        const raw = await urgencyConfigService.getAll();
        this.urgencyConfigs = raw.docs || raw;
      } catch (e) {
        this.$message.error("Error al cargar especialidades");
      }
    },

    /**
     * @description Busca pacientes con debounce de 300ms
     * @param {string} query - Texto de búsqueda
     */
    onSearchInput(query) {
      if (this.searchTimeout) clearTimeout(this.searchTimeout);
      this.searchQuery = query;

      if (!query || query.length < 2) {
        this.searchResults = [];
        return;
      }

      this.searchTimeout = setTimeout(() => {
        this.doSearch(query);
      }, 300);
    },

    /**
     * @description Ejecuta la búsqueda de pacientes
     * @param {string} query - Texto de búsqueda
     * @returns {Promise<void>}
     */
    async doSearch(query) {
      this.isSearching = true;
      try {
        this.searchResults = await urgencyQueueService.searchPatient(query);
      } catch (e) {
        this.searchResults = [];
      } finally {
        this.isSearching = false;
      }
    },

    /**
     * @description Obtiene las iniciales de un paciente para el avatar
     * @param {Object} patient - Paciente
     * @returns {string}
     */
    getInitials(patient) {
      const first = (patient.firstName || "")[0] || "";
      const last = (patient.lastName || "")[0] || "";
      return (first + last).toUpperCase();
    },

    /**
     * @description Obtiene el nombre completo formateado de un paciente
     * @param {Object} patient - Paciente
     * @returns {string}
     */
    getFullName(patient) {
      return (
        `${patient.firstName || ""} ${patient.lastName || ""}`.trim() || "—"
      );
    },

    /**
     * @description Obtiene el DNI formateado de un paciente
     * @param {Object} patient - Paciente
     * @returns {string}
     */
    getDni(patient) {
      return patient.dni || patient.governmentId?.number || "";
    },

    /**
     * @description Selecciona un paciente existente
     * @param {Object} patient - Paciente seleccionado
     */
    selectPatient(patient) {
      this.selectedPatient = patient;
      this.isNewPatient = false;
      this.searchResults = [];
      this.searchQuery = "";
    },

    /**
     * @description Deselecciona el paciente actual
     */
    clearPatient() {
      this.selectedPatient = null;
      this.isNewPatient = false;
      this.newPatientForm = {
        firstName: "",
        lastName: "",
        email: "",
        governmentIdNumber: "",
        phone: "",
      };
      this.newPatientErrors = {};
    },

    /**
     * @description Activa el formulario de paciente nuevo
     */
    showNewPatientForm() {
      this.isNewPatient = true;
      this.selectedPatient = null;
      this.searchResults = [];
      this.searchQuery = "";
    },

    /**
     * @description Valida los datos del paciente nuevo
     * @returns {boolean}
     */
    validateNewPatient() {
      this.newPatientErrors = {};
      const f = this.newPatientForm;
      if (!f.firstName) this.newPatientErrors.firstName = "Nombre requerido";
      if (!f.lastName) this.newPatientErrors.lastName = "Apellido requerido";
      if (!f.email) this.newPatientErrors.email = "Email requerido";
      if (!f.governmentIdNumber)
        this.newPatientErrors.governmentIdNumber = "DNI requerido";
      return Object.keys(this.newPatientErrors).length === 0;
    },

    /**
     * @description Avanza al paso 2
     */
    goToStep2() {
      if (this.isNewPatient && !this.validateNewPatient()) return;
      if (!this.isStep1Valid) return;
      this.step = 2;
    },

    /**
     * @description Avanza al paso 3 (resumen)
     */
    goToStep3() {
      this.formErrors = {};
      if (!this.medicalSpecialtyId) {
        this.formErrors.medicalSpecialtyId = "Seleccione una especialidad";
        return;
      }
      this.step = 3;
    },

    /**
     * @description Envía el caso de urgencia al backend
     * @returns {Promise<void>}
     */
    async submitCase() {
      this.isSubmitting = true;
      try {
        const payload = {
          medicalSpecialtyId: this.medicalSpecialtyId,
          reason: this.reason,
        };

        if (this.selectedPatient) {
          payload.patientId = this.selectedPatient._id;
        } else if (this.isNewPatient) {
          const f = this.newPatientForm;
          payload.newPatient = {
            firstName: f.firstName,
            lastName: f.lastName,
            email: f.email,
            governmentId: {
              type: "dni",
              number: f.governmentIdNumber,
            },
          };
          if (f.phone) {
            payload.newPatient.phone = f.phone;
          }
        }

        await urgencyQueueService.createCase(payload);
        this.$message.success("Caso de urgencia creado exitosamente");
        this.$router.push({ name: "urgency-queue" });
      } catch (e) {
        const msg =
          e?.response?.data?.message || "Error al crear caso de urgencia";
        this.$message.error(msg);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template lang="pug">
section.cc-page
  BaseSubNav(title="Nuevo caso de urgencia")

  //- Stepper
  .cc-stepper
    .cc-stepper__step(:class="{ 'is-active': step === 1, 'is-done': step > 1 }" @click="step > 1 ? step = 1 : null")
      .cc-stepper__circle
        micon(v-if="step > 1" name="check")
        span(v-else) 1
      .cc-stepper__label Paciente
    .cc-stepper__line(:class="{ 'is-done': step > 1 }")
    .cc-stepper__step(:class="{ 'is-active': step === 2, 'is-done': step > 2 }" @click="step > 2 ? step = 2 : null")
      .cc-stepper__circle
        micon(v-if="step > 2" name="check")
        span(v-else) 2
      .cc-stepper__label Especialidad
    .cc-stepper__line(:class="{ 'is-done': step > 2 }")
    .cc-stepper__step(:class="{ 'is-active': step === 3 }")
      .cc-stepper__circle
        span 3
      .cc-stepper__label Confirmar

  //- ════════════════════════════════════════
  //- PASO 1 — Buscar / Crear paciente
  //- ════════════════════════════════════════
  .cc-card(v-if="step === 1")
    .cc-card__header
      h2.cc-card__title Buscar paciente
      .cc-card__header-sep

    //- Patient selected — success card
    .cc-selected(v-if="selectedPatient")
      .cc-selected__left
        .cc-selected__check
          micon(name="check")
        .cc-selected__info
          .cc-selected__name {{ patientDisplayName }}
          .cc-selected__meta
            span(v-if="patientDisplayDni !== '—'") DNI {{ patientDisplayDni }}
            span(v-if="selectedPatient.email")  &middot; {{ selectedPatient.email }}
      button.cc-selected__clear(@click="clearPatient" title="Cambiar paciente")
        micon(name="close")

    //- Search toolbar (style: PatientList page-toolbar)
    .cc-toolbar(v-if="!selectedPatient && !isNewPatient")
      button.cc-btn-navy(@click="showNewPatientForm")
        micon(name="add")
        span Nuevo Paciente
      .cc-search
        el-input(
          :value="searchQuery"
          @input="onSearchInput"
          placeholder="Buscar por nombre, documento..."
          prefix-icon="el-icon-search"
          size="small"
          clearable
        )

    //- Search results
    .cc-results(v-if="!selectedPatient && !isNewPatient && (searchResults.length || isSearching || (searchQuery.length >= 2 && !searchResults.length))")
      .cc-results__loading(v-if="isSearching")
        span Buscando...

      template(v-else-if="searchResults.length")
        .cc-result(
          v-for="patient in searchResults"
          :key="patient._id"
          @click="selectPatient(patient)"
        )
          .cc-result__avatar {{ getInitials(patient) }}
          .cc-result__info
            .cc-result__name {{ getFullName(patient) }}
            .cc-result__meta
              span(v-if="getDni(patient)") DNI {{ getDni(patient) }}
              span(v-if="patient.email")  &middot; {{ patient.email }}

      .cc-results__empty(v-else-if="searchQuery.length >= 2 && !isSearching")
        p No se encontraron pacientes con "{{ searchQuery }}"

    //- New patient form
    .cc-new-patient(v-if="isNewPatient && !selectedPatient")
      .cc-new-patient__header
        h3.cc-new-patient__title Datos del nuevo paciente
        button.cc-new-patient__cancel(@click="clearPatient")
          micon(name="close")
          span Cancelar

      .cc-new-patient__grid
        .cc-new-patient__field
          label.cc-label Nombre *
          el-input(v-model="newPatientForm.firstName" placeholder="Nombre" size="small")
          .cc-field-error(v-if="newPatientErrors.firstName") {{ newPatientErrors.firstName }}

        .cc-new-patient__field
          label.cc-label Apellido *
          el-input(v-model="newPatientForm.lastName" placeholder="Apellido" size="small")
          .cc-field-error(v-if="newPatientErrors.lastName") {{ newPatientErrors.lastName }}

        .cc-new-patient__field
          label.cc-label Email *
          el-input(v-model="newPatientForm.email" placeholder="email@ejemplo.com" size="small" type="email")
          .cc-field-error(v-if="newPatientErrors.email") {{ newPatientErrors.email }}

        .cc-new-patient__field
          label.cc-label DNI *
          el-input(v-model="newPatientForm.governmentIdNumber" placeholder="12345678" size="small" type="number")
          .cc-field-error(v-if="newPatientErrors.governmentIdNumber") {{ newPatientErrors.governmentIdNumber }}

        .cc-new-patient__field
          label.cc-label Teléfono (opcional)
          el-input(v-model="newPatientForm.phone" placeholder="11-1234-5678" size="small")

    //- Step 1 actions
    .cc-actions(v-if="step === 1")
      .cc-actions__spacer
      button.cc-btn-teal(:disabled="!isStep1Valid" @click="goToStep2")
        span Continuar
        micon(name="chevron_right")

  //- ════════════════════════════════════════
  //- PASO 2 — Especialidad + motivo
  //- ════════════════════════════════════════
  .cc-card(v-if="step === 2")
    .cc-card__header
      h2.cc-card__title Seleccioná una especialidad
      p.cc-card__subtitle Elegí la especialidad para la consulta de urgencia
      .cc-card__header-sep

    //- Specialty cards
    .cc-specialties
      .cc-specialty(
        v-for="config in enabledConfigs"
        :key="config.medicalSpecialty._id"
        :class="{ 'is-selected': medicalSpecialtyId === config.medicalSpecialty._id }"
        @click="medicalSpecialtyId = config.medicalSpecialty._id"
      )
        .cc-specialty__icon
          micon(name="medication")
        .cc-specialty__name {{ config.medicalSpecialty.name }}
        .cc-specialty__check(v-if="medicalSpecialtyId === config.medicalSpecialty._id")
          micon(name="check")

    .cc-field-error.cc-field-error--center(v-if="formErrors.medicalSpecialtyId") {{ formErrors.medicalSpecialtyId }}

    //- Reason
    .cc-reason
      label.cc-label Motivo de la consulta (opcional)
      el-input(
        type="textarea"
        v-model="reason"
        placeholder="Describir brevemente el motivo de la consulta..."
        :rows="3"
        maxlength="500"
        show-word-limit
        size="small"
      )

    //- Step 2 actions
    .cc-actions
      button.cc-btn-ghost(@click="step = 1")
        micon(name="chevron_left")
        span Anterior
      button.cc-btn-teal(:disabled="!isStep2Valid" @click="goToStep3")
        span Continuar
        micon(name="chevron_right")

  //- ════════════════════════════════════════
  //- PASO 3 — Confirmar
  //- ════════════════════════════════════════
  .cc-card(v-if="step === 3")
    .cc-card__header
      h2.cc-card__title Confirmar caso de urgencia
      p.cc-card__subtitle Revisá los datos antes de crear el caso
      .cc-card__header-sep

    .cc-summary
      .cc-summary__section
        .cc-summary__section-title Paciente
        .cc-summary__row
          .cc-summary__avatar {{ getInitials(selectedPatient || newPatientForm) }}
          .cc-summary__patient-info
            .cc-summary__patient-name {{ patientDisplayName }}
            .cc-summary__patient-meta
              span(v-if="patientDisplayDni !== '—'") DNI {{ patientDisplayDni }}
              span(v-if="patientDisplayEmail")  &middot; {{ patientDisplayEmail }}
        .cc-summary__new-badge(v-if="isNewPatient")
          micon(name="person_add")
          span Se creará un paciente nuevo

      .cc-summary__divider

      .cc-summary__section
        .cc-summary__section-title Especialidad
        .cc-summary__detail
          .cc-summary__detail-icon
            micon(name="medication")
          span {{ selectedSpecialtyName }}

      .cc-summary__divider(v-if="reason")

      .cc-summary__section(v-if="reason")
        .cc-summary__section-title Motivo
        p.cc-summary__reason {{ reason }}

    //- Step 3 actions
    .cc-actions
      button.cc-btn-ghost(@click="step = 2")
        micon(name="chevron_left")
        span Anterior
      button.cc-btn-teal.cc-btn-teal--lg(:disabled="isSubmitting" @click="submitCase")
        template(v-if="isSubmitting")
          span Creando...
        template(v-else)
          micon(name="add_circle_outline")
          span Crear caso
</template>

<style lang="scss" scoped>
/* ═══════════════════════════════════════════
   Page layout
   ═══════════════════════════════════════════ */
.cc-page {
  padding: 0;
}

/* ═══════════════════════════════════════════
   Stepper — teal active, navy done, gray future
   ═══════════════════════════════════════════ */
.cc-stepper {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  max-width: 420px;
  margin: 28px auto 24px;

  &__step {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    cursor: default;

    &.is-done {
      cursor: pointer;
    }
  }

  &__circle {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    background: #e2e8f0;
    color: $text-muted-new;
    transition: all 0.25s ease;

    .micon {
      width: 18px;
      height: 18px;
      fill: #fff;
    }

    .is-active & {
      background: #0d9d9d;
      color: #fff;
      box-shadow: 0 0 0 4px rgba(13, 157, 157, 0.15);
    }

    .is-done & {
      background: $navy;
      color: #fff;
    }
  }

  &__label {
    font-size: 12px;
    font-weight: 500;
    color: $text-muted-new;
    white-space: nowrap;
    transition: all 0.2s ease;

    .is-active & {
      color: #0d9d9d;
      font-weight: 600;
    }

    .is-done & {
      color: $navy;
      font-weight: 600;
    }
  }

  &__line {
    flex: 1;
    height: 2px;
    background: #e2e8f0;
    min-width: 48px;
    margin: 18px 12px 0;
    transition: background 0.25s ease;

    &.is-done {
      background: $navy;
    }
  }
}

/* ═══════════════════════════════════════════
   Card container
   ═══════════════════════════════════════════ */
.cc-card {
  background: #fff;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 28px 32px 24px;
  max-width: 680px;
  margin: 0 auto;

  &__header {
    margin-bottom: 20px;
  }

  &__title {
    font-size: 18px;
    font-weight: 700;
    color: $navy;
    margin: 0 0 4px;
  }

  &__subtitle {
    font-size: 14px;
    color: $text-secondary-new;
    margin: 0 0 16px;
  }

  &__header-sep {
    height: 1px;
    background: $border-color;
  }
}

/* ═══════════════════════════════════════════
   Step 1 — Toolbar (matches PatientList)
   ═══════════════════════════════════════════ */
.cc-toolbar {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 20px;
}

.cc-search {
  flex: 1;
  max-width: 340px;

  ::v-deep .el-input__inner {
    border-radius: 8px;
    border: 1px solid $border-color;
    font-size: 13px;

    &:focus {
      border-color: #0d9d9d;
      box-shadow: 0 0 0 3px rgba(13, 157, 157, 0.1);
    }
  }
}

/* ═══════════════════════════════════════════
   Search results
   ═══════════════════════════════════════════ */
.cc-results {
  margin-top: 12px;
  border: 1px solid $border-color;
  border-radius: 12px;
  overflow: hidden;
  max-height: 280px;
  overflow-y: auto;

  &__loading,
  &__empty {
    padding: 16px 20px;
    text-align: center;
    color: $text-muted-new;
    font-size: 13px;

    p {
      margin: 0;
    }
  }
}

.cc-result {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 20px;
  cursor: pointer;
  border-bottom: 1px solid #f1f5f9;
  transition: background 0.12s ease;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: $teal-light;
  }

  &__avatar {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: $teal-light;
    color: $navy;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: 700;
    flex-shrink: 0;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 600;
    color: $navy;
  }

  &__meta {
    font-size: 12px;
    color: $text-secondary-new;
    margin-top: 1px;
  }
}

/* ═══════════════════════════════════════════
   Selected patient — success card
   ═══════════════════════════════════════════ */
.cc-selected {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 20px;
  padding: 14px 20px;
  background: $teal-light;
  border: 1px solid rgba(5, 229, 186, 0.3);
  border-radius: 12px;

  &__left {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__check {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #0d9d9d;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .micon {
      width: 18px;
      height: 18px;
      fill: #fff;
    }
  }

  &__info {
    min-width: 0;
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
    color: $navy;
  }

  &__meta {
    font-size: 12px;
    color: $text-secondary-new;
    margin-top: 2px;
  }

  &__clear {
    background: none;
    border: none;
    cursor: pointer;
    padding: 6px;
    border-radius: 6px;
    transition: background 0.15s;

    .micon {
      width: 16px;
      height: 16px;
      fill: $text-muted-new;
    }

    &:hover {
      background: rgba(0, 0, 0, 0.06);

      .micon {
        fill: $text-primary-new;
      }
    }
  }
}

/* ═══════════════════════════════════════════
   New patient form
   ═══════════════════════════════════════════ */
.cc-new-patient {
  margin-top: 20px;
  padding: 20px;
  background: $header-bg;
  border: 1px solid $border-color;
  border-radius: 12px;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 15px;
    font-weight: 600;
    color: $navy;
    margin: 0;
  }

  &__cancel {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: none;
    border: none;
    cursor: pointer;
    font-size: 13px;
    color: $text-secondary-new;
    padding: 4px 8px;
    border-radius: 6px;
    transition: all 0.15s;

    .micon {
      width: 14px;
      height: 14px;
      fill: $text-secondary-new;
    }

    &:hover {
      background: #f1f5f9;
      color: $text-primary-new;

      .micon {
        fill: $text-primary-new;
      }
    }
  }

  &__grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 16px;
  }
}

.cc-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: $text-secondary-new;
  margin-bottom: 4px;
}

.cc-field-error {
  font-size: 12px;
  color: #ef4444;
  margin-top: 4px;

  &--center {
    text-align: center;
    margin-top: 8px;
  }
}

/* ═══════════════════════════════════════════
   Step 2 — Specialty cards (landing style)
   ═══════════════════════════════════════════ */
.cc-specialties {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 20px;
}

.cc-specialty {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 16px 20px;
  border: 1.5px solid $border-color;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  background: #fff;

  &:hover {
    border-color: rgba(13, 157, 157, 0.4);
    background: rgba(13, 157, 157, 0.02);
  }

  &.is-selected {
    border-color: #0d9d9d;
    background: rgba(13, 157, 157, 0.05);
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: $teal-light;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .micon {
      width: 20px;
      height: 20px;
      fill: #0d9d9d;
    }

    .is-selected & {
      background: #0d9d9d;

      .micon {
        fill: #fff;
      }
    }
  }

  &__name {
    flex: 1;
    font-size: 15px;
    font-weight: 600;
    color: $navy;
  }

  &__check {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: #0d9d9d;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    .micon {
      width: 14px;
      height: 14px;
      fill: #fff;
    }
  }
}

/* ═══════════════════════════════════════════
   Reason textarea
   ═══════════════════════════════════════════ */
.cc-reason {
  margin-top: 24px;

  ::v-deep .el-textarea__inner {
    border-radius: 10px;
    border: 1px solid $border-color;
    font-size: 13px;
    padding: 12px 14px;

    &:focus {
      border-color: #0d9d9d;
      box-shadow: 0 0 0 3px rgba(13, 157, 157, 0.1);
    }
  }
}

/* ═══════════════════════════════════════════
   Step 3 — Summary
   ═══════════════════════════════════════════ */
.cc-summary {
  margin-top: 20px;
  background: $header-bg;
  border: 1px solid $border-color;
  border-radius: 12px;
  padding: 24px;

  &__section {
    margin-bottom: 0;
  }

  &__section-title {
    font-size: 11px;
    font-weight: 700;
    color: $text-muted-new;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    margin-bottom: 10px;
  }

  &__divider {
    height: 1px;
    background: $border-color;
    margin: 16px 0;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  &__avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: $teal-light;
    color: $navy;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
  }

  &__patient-info {
    flex: 1;
  }

  &__patient-name {
    font-size: 15px;
    font-weight: 700;
    color: $navy;
  }

  &__patient-meta {
    font-size: 12px;
    color: $text-secondary-new;
    margin-top: 2px;
  }

  &__new-badge {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 10px;
    padding: 5px 12px;
    background: rgba(245, 158, 11, 0.08);
    border-radius: 8px;
    font-size: 12px;
    color: #b45309;
    font-weight: 600;

    .micon {
      width: 14px;
      height: 14px;
      fill: #f59e0b;
    }
  }

  &__detail {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
    font-weight: 600;
    color: $navy;

    &-icon {
      width: 36px;
      height: 36px;
      border-radius: 8px;
      background: $teal-light;
      display: flex;
      align-items: center;
      justify-content: center;

      .micon {
        width: 18px;
        height: 18px;
        fill: #0d9d9d;
      }
    }
  }

  &__reason {
    font-size: 14px;
    color: $text-primary-new;
    line-height: 1.5;
    margin: 0;
    white-space: pre-wrap;
    word-break: break-word;
  }
}

/* ═══════════════════════════════════════════
   Action bar
   ═══════════════════════════════════════════ */
.cc-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid $border-color;

  &__spacer {
    flex: 1;
  }
}

/* ═══════════════════════════════════════════
   Buttons
   ═══════════════════════════════════════════ */
.cc-btn-navy {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: $navy;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
  white-space: nowrap;

  .micon {
    width: 16px;
    height: 16px;
    fill: #fff;
  }

  &:hover {
    background: darken(#002d5e, 8%);
  }
}

.cc-btn-teal {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 10px 24px;
  background: #0d9d9d;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  .micon {
    width: 16px;
    height: 16px;
    fill: #fff;
  }

  &:hover:not(:disabled) {
    background: darken(#0d9d9d, 8%);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &--lg {
    padding: 12px 32px;
    font-size: 15px;
  }
}

.cc-btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background: transparent;
  color: $text-secondary-new;
  border: 1px solid $border-color;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;

  .micon {
    width: 14px;
    height: 14px;
    fill: $text-secondary-new;
  }

  &:hover {
    background: #f1f5f9;
    color: $text-primary-new;
    border-color: darken($border-color, 8%);

    .micon {
      fill: $text-primary-new;
    }
  }
}
</style>
